import { useState, type ReactElement } from 'react';
import { Check, X, Sparkles } from 'lucide-react';

interface PricingFeature {
  name: string;
  starter: boolean | string;
  plus: boolean | string;
  professional: boolean | string;
  business: boolean | string;
  tooltip?: string;
}

interface FeatureCategory {
  category: string;
  features: PricingFeature[];
}

interface PricingTier {
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  highlighted?: boolean;
  cta: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Starter',
    description: 'Zum Ausprobieren',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: [
      'Bis zu 5 Rechnungen/Monat',
      '20 Belege/Monat',
      '1 Benutzer',
      'Basis-Dashboard',
      'E-Mail-Support',
    ],
    cta: 'Kostenlos starten',
  },
  {
    name: 'Plus',
    description: 'Für Freelancer & Kleinunternehmer',
    monthlyPrice: 12,
    yearlyPrice: 9,
    features: [
      'Bis zu 30 Rechnungen/Monat',
      '100 Belege/Monat',
      'KI-Belegerkennung',
      'Kundenverwaltung',
      'Rechnungsvorlagen',
      'Kassenbuch',
      'EÜR-Auswertung',
      'Mobile App',
    ],
    cta: 'Jetzt testen',
  },
  {
    name: 'Professional',
    description: 'Für wachsende Unternehmen',
    monthlyPrice: 29,
    yearlyPrice: 24,
    features: [
      'Unbegrenzte Rechnungen',
      'Unbegrenzte Belege',
      'Bis zu 5 Benutzer',
      'KI-Belegerkennung',
      'Banking-Integration',
      'Mahnwesen',
      'Alle Steuermodule',
      'ELSTER-Anbindung',
      'BWA & Reports',
    ],
    highlighted: true,
    cta: 'Beliebteste Wahl',
  },
  {
    name: 'Business',
    description: 'Für Teams & Unternehmen',
    monthlyPrice: 59,
    yearlyPrice: 49,
    features: [
      'Alles aus Professional',
      'Unbegrenzte Benutzer',
      'Multi-Mandanten',
      'API-Zugang',
      'DATEV-Export',
      'Dedizierter Support',
      'SLA-Garantie',
      'On-Premise Option',
    ],
    cta: 'Kontakt aufnehmen',
  },
];

const featureCategories: FeatureCategory[] = [
  {
    category: 'Buchhaltung',
    features: [
      { name: 'Belege erfassen', starter: '20/Monat', plus: '100/Monat', professional: 'Unbegrenzt', business: 'Unbegrenzt' },
      { name: 'KI-Belegerkennung (Foto/PDF)', starter: false, plus: true, professional: true, business: true },
      { name: 'Kassenbuch', starter: false, plus: true, professional: true, business: true },
      { name: 'Abschreibungen (AfA)', starter: false, plus: false, professional: true, business: true },
      { name: 'Nachweise verwalten', starter: false, plus: 'Basis', professional: 'Erweitert', business: 'Erweitert' },
      { name: 'Datenimport (CSV, Excel)', starter: false, plus: false, professional: true, business: true },
      { name: 'Automatische Kategorisierung', starter: false, plus: false, professional: 'KI-gestützt', business: 'KI-gestützt' },
    ],
  },
  {
    category: 'Rechnungen',
    features: [
      { name: 'Rechnungen erstellen', starter: '5/Monat', plus: '30/Monat', professional: 'Unbegrenzt', business: 'Unbegrenzt' },
      { name: 'Rechnungsvorlagen', starter: '1 Vorlage', plus: '5 Vorlagen', professional: 'Unbegrenzt', business: 'Unbegrenzt + Individuell' },
      { name: 'Kundenverwaltung', starter: '10 Kunden', plus: '50 Kunden', professional: 'Unbegrenzt', business: 'Unbegrenzt' },
      { name: 'Angebote erstellen', starter: false, plus: true, professional: true, business: true },
      { name: 'Mahnwesen', starter: false, plus: false, professional: true, business: true },
      { name: 'Automatische Mahnstufen', starter: false, plus: false, professional: '3 Stufen', business: 'Individuell' },
      { name: 'Banking-Integration', starter: false, plus: false, professional: true, business: true },
      { name: 'Wiederkehrende Rechnungen', starter: false, plus: false, professional: true, business: true },
    ],
  },
  {
    category: 'Steuern',
    features: [
      { name: 'Umsatzsteuer-Übersicht', starter: 'Basis', plus: true, professional: true, business: true },
      { name: 'UStVA-Erstellung', starter: false, plus: false, professional: true, business: true },
      { name: 'Einkommensteuer-Modul', starter: false, plus: false, professional: true, business: true },
      { name: 'Gewerbesteuer-Modul', starter: false, plus: false, professional: true, business: true },
      { name: 'ELSTER-Anbindung', starter: false, plus: false, professional: true, business: true },
      { name: 'Bescheinigungen abrufen', starter: false, plus: false, professional: true, business: true },
      { name: 'Belege nachreichen', starter: false, plus: false, professional: true, business: true },
      { name: 'Mitteilungen vom Finanzamt', starter: false, plus: false, professional: true, business: true },
    ],
  },
  {
    category: 'Auswertungen',
    features: [
      { name: 'Dashboard & Übersicht', starter: 'Basis', plus: 'Standard', professional: 'Erweitert', business: 'Premium' },
      { name: 'BWA erstellen', starter: false, plus: false, professional: true, business: true },
      { name: 'Summen- und Saldenliste', starter: false, plus: false, professional: true, business: true },
      { name: 'Kennzahlen & KPIs', starter: false, plus: false, professional: 'Standard', business: 'Individuell' },
      { name: 'EÜR (Einnahmen-Überschuss)', starter: false, plus: 'Basis', professional: 'Vollständig', business: 'Vollständig' },
      { name: 'Export (PDF, CSV, DATEV)', starter: 'PDF', plus: 'PDF, CSV', professional: 'Alle Formate', business: 'Alle + API' },
    ],
  },
  {
    category: 'Formulare',
    features: [
      { name: 'Stundungsantrag', starter: false, plus: false, professional: true, business: true },
      { name: 'Einspruch', starter: false, plus: false, professional: true, business: true },
      { name: 'Ratenzahlung', starter: false, plus: false, professional: true, business: true },
      { name: 'Fristverlängerung', starter: false, plus: false, professional: true, business: true },
      { name: 'Berichtigung', starter: false, plus: false, professional: true, business: true },
      { name: 'Vorausgefüllte Formulare', starter: false, plus: false, professional: true, business: true },
    ],
  },
  {
    category: 'System & Support',
    features: [
      { name: 'Benutzer', starter: '1', plus: '2', professional: '5', business: 'Unbegrenzt' },
      { name: 'Mobile App (iOS & Android)', starter: false, plus: true, professional: true, business: true },
      { name: 'API-Zugang', starter: false, plus: false, professional: false, business: true },
      { name: 'Multi-Mandanten', starter: false, plus: false, professional: false, business: true },
      { name: 'Steuerberater-Zugang', starter: false, plus: false, professional: true, business: true },
      { name: 'Support', starter: 'E-Mail', plus: 'E-Mail', professional: 'E-Mail + Chat', business: 'Dediziert + Telefon' },
      { name: 'Reaktionszeit Support', starter: '72h', plus: '48h', professional: '24h', business: '4h (SLA)' },
      { name: 'Onboarding & Schulung', starter: 'Hilfe-Center', plus: 'Hilfe-Center', professional: 'Webinar', business: 'Persönlich' },
      { name: 'Datenexport bei Kündigung', starter: true, plus: true, professional: true, business: true },
    ],
  },
  {
    category: 'Sicherheit & Compliance',
    features: [
      { name: 'DSGVO-konform', starter: true, plus: true, professional: true, business: true },
      { name: 'GoBD-zertifiziert', starter: true, plus: true, professional: true, business: true },
      { name: 'SSL-Verschlüsselung', starter: true, plus: true, professional: true, business: true },
      { name: 'Server in Deutschland', starter: true, plus: true, professional: true, business: true },
      { name: 'Zwei-Faktor-Authentifizierung', starter: false, plus: true, professional: true, business: true },
      { name: 'Audit-Log', starter: false, plus: false, professional: true, business: true },
      { name: 'Rollen & Berechtigungen', starter: false, plus: false, professional: 'Basis', business: 'Erweitert' },
      { name: 'On-Premise Option', starter: false, plus: false, professional: false, business: true },
    ],
  },
];

export default function Pricing(): ReactElement {
  const [isYearly, setIsYearly] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    featureCategories.map((c) => c.category)
  );

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const renderFeatureValue = (value: boolean | string, highlight?: boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="w-5 h-5 text-success-500 mx-auto" />
      ) : (
        <X className="w-5 h-5 text-secondary-300 mx-auto" />
      );
    }
    return (
      <span className={highlight ? 'text-primary-700 font-medium' : 'text-secondary-700'}>
        {value}
      </span>
    );
  };

  return (
    <section id="preise" className="py-20 lg:py-32 bg-white relative overflow-hidden">
      {/* Background Elements */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-primary-600 font-semibold text-sm tracking-wider uppercase mb-4">
            Preise
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
            Transparente Preise für jede Unternehmensgröße
          </h2>
          <p className="text-lg text-secondary-600">
            Starten Sie kostenlos und upgraden Sie, wenn Ihr Unternehmen wächst.
            Keine versteckten Kosten.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span
            className={`text-sm font-medium transition-colors ${
              !isYearly ? 'text-secondary-900' : 'text-secondary-500'
            }`}
          >
            Monatlich
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              isYearly ? 'bg-primary-600' : 'bg-secondary-300'
            }`}
            aria-label="Zahlungsintervall wechseln"
          >
            <span
              className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                isYearly ? 'left-8' : 'left-1'
              }`}
            />
          </button>
          <span
            className={`text-sm font-medium transition-colors ${
              isYearly ? 'text-secondary-900' : 'text-secondary-500'
            }`}
          >
            Jährlich
          </span>
          <span className="bg-success-100 text-success-700 text-xs font-semibold px-2 py-1 rounded-full">
            2 Monate gratis
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {pricingTiers.map((tier, index) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                tier.highlighted
                  ? 'bg-primary-600 text-white shadow-2xl shadow-primary-600/30 scale-105 z-10'
                  : 'bg-white border border-secondary-200 hover:border-primary-200 hover:shadow-xl'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Popular Badge */}
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-warning-400 text-warning-900 px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  Beliebteste Wahl
                </div>
              )}

              {/* Tier Name */}
              <h3
                className={`text-xl font-semibold mb-2 ${
                  tier.highlighted ? 'text-white' : 'text-secondary-900'
                }`}
              >
                {tier.name}
              </h3>
              <p
                className={`text-sm mb-6 ${
                  tier.highlighted ? 'text-primary-100' : 'text-secondary-500'
                }`}
              >
                {tier.description}
              </p>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span
                    className={`text-4xl font-bold ${
                      tier.highlighted ? 'text-white' : 'text-secondary-900'
                    }`}
                  >
                    {tier.monthlyPrice === 0
                      ? 'Kostenlos'
                      : `${isYearly ? tier.yearlyPrice : tier.monthlyPrice}€`}
                  </span>
                  {tier.monthlyPrice > 0 && (
                    <span
                      className={`text-sm ${
                        tier.highlighted ? 'text-primary-200' : 'text-secondary-500'
                      }`}
                    >
                      /Monat
                    </span>
                  )}
                </div>
                {tier.monthlyPrice > 0 && isYearly && (
                  <p
                    className={`text-sm mt-1 ${
                      tier.highlighted ? 'text-primary-200' : 'text-secondary-500'
                    }`}
                  >
                    {tier.yearlyPrice * 12}€ jährlich abgerechnet
                  </p>
                )}
              </div>

              {/* CTA Button */}
              <a
                href="#signup"
                className={`block w-full text-center py-3 rounded-lg font-semibold transition-all mb-8 ${
                  tier.highlighted
                    ? 'bg-white text-primary-600 hover:bg-primary-50'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {tier.cta}
              </a>

              {/* Features */}
              <ul className="space-y-4">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        tier.highlighted ? 'text-primary-200' : 'text-success-500'
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        tier.highlighted ? 'text-primary-100' : 'text-secondary-600'
                      }`}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Detailed Feature Comparison Table */}
        <div className="bg-secondary-50 rounded-2xl p-6 lg:p-8">
          <h3 className="text-2xl lg:text-3xl font-bold text-secondary-900 mb-2 text-center">
            Detaillierter Funktionsvergleich
          </h3>
          <p className="text-secondary-600 text-center mb-8">
            Alle {featureCategories.reduce((acc, cat) => acc + cat.features.length, 0)} Funktionen im Überblick
          </p>

          {/* Sticky Header for Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-secondary-100">
                  <th className="text-left py-4 px-3 font-semibold text-secondary-900 rounded-tl-lg w-[28%]">
                    Funktion
                  </th>
                  <th className="text-center py-4 px-2 font-semibold text-secondary-700 w-[16%]">
                    <div className="flex flex-col items-center">
                      <span className="text-sm">Starter</span>
                      <span className="text-xs font-normal text-secondary-500">Kostenlos</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-2 font-semibold text-secondary-700 w-[16%]">
                    <div className="flex flex-col items-center">
                      <span className="text-sm">Plus</span>
                      <span className="text-xs font-normal text-secondary-500">{isYearly ? '9' : '12'}€/Monat</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-2 font-semibold text-primary-600 bg-primary-100/50 w-[16%]">
                    <div className="flex flex-col items-center">
                      <span className="text-sm">Professional</span>
                      <span className="text-xs font-normal text-primary-500">{isYearly ? '24' : '29'}€/Monat</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-2 font-semibold text-secondary-700 rounded-tr-lg w-[16%]">
                    <div className="flex flex-col items-center">
                      <span className="text-sm">Business</span>
                      <span className="text-xs font-normal text-secondary-500">{isYearly ? '49' : '59'}€/Monat</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {featureCategories.map((category) => (
                  <>
                    {/* Category Header */}
                    <tr
                      key={category.category}
                      className="bg-secondary-200/50 cursor-pointer hover:bg-secondary-200 transition-colors"
                      onClick={() => toggleCategory(category.category)}
                    >
                      <td
                        colSpan={5}
                        className="py-3 px-4 font-semibold text-secondary-800"
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className={`w-4 h-4 transition-transform ${
                              expandedCategories.includes(category.category) ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                          {category.category}
                          <span className="text-xs font-normal text-secondary-500">
                            ({category.features.length} Funktionen)
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Category Features */}
                    {expandedCategories.includes(category.category) &&
                      category.features.map((feature, index) => (
                        <tr
                          key={`${category.category}-${feature.name}`}
                          className={`${
                            index % 2 === 0 ? 'bg-white' : 'bg-secondary-50/50'
                          } hover:bg-secondary-100/50 transition-colors`}
                        >
                          <td className="py-3 px-3 pl-8 text-secondary-700 text-sm">
                            {feature.name}
                          </td>
                          <td className="py-3 px-2 text-center text-sm">
                            {renderFeatureValue(feature.starter)}
                          </td>
                          <td className="py-3 px-2 text-center text-sm">
                            {renderFeatureValue(feature.plus)}
                          </td>
                          <td className="py-3 px-2 text-center text-sm bg-primary-50/30">
                            {renderFeatureValue(feature.professional, true)}
                          </td>
                          <td className="py-3 px-2 text-center text-sm">
                            {renderFeatureValue(feature.business)}
                          </td>
                        </tr>
                      ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Expand/Collapse All */}
          <div className="flex justify-center mt-6 gap-4">
            <button
              onClick={() => setExpandedCategories(featureCategories.map((c) => c.category))}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Alle aufklappen
            </button>
            <span className="text-secondary-300">|</span>
            <button
              onClick={() => setExpandedCategories([])}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Alle zuklappen
            </button>
          </div>
        </div>

        {/* FAQ-like section */}
        <div className="mt-16 text-center">
          <p className="text-secondary-600 mb-4">
            Noch Fragen? Wir beraten Sie gerne.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#kontakt"
              className="btn-outline px-6 py-3"
            >
              Beratungsgespräch vereinbaren
            </a>
            <a
              href="#signup"
              className="btn-primary px-6 py-3"
            >
              14 Tage kostenlos testen
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
